import { IssuerId } from "@goodfill/domain-properties";
import { PricedBond } from "../../PricedBond";

type PaymentRank = PricedBond["paymentRank"];

export type IssuersWithPaymentRanksMap = Map<IssuerId, PaymentRank[]>;

export interface IssuerEntity {
  id: IssuerId;
  name: string;
  ticker: string;
}

export type IssuerWithPaymentRank = IssuerEntity & {
  paymentRank?: PaymentRank;
};

export const issuerIdWithPaymentRank = (issuerId: IssuerId, paymentRank: PaymentRank) =>
  `${issuerId}-${paymentRank}` as unknown as IssuerId;

export type GetIssuersWithPaymentRankInputBond = Pick<
  PricedBond,
  "issuerId" | "issuerTicker" | "issuerName" | "paymentRank"
>;

export function getIssuersWithPaymentRank(
  pricedBonds: Array<GetIssuersWithPaymentRankInputBond>,
): IssuerWithPaymentRank[] {
  const issuersWithPaymentRanks = pricedBonds.reduce<IssuersWithPaymentRanksMap>((result, bond) => {
    const issuerId = bond.issuerId;
    const paymentRank = bond.paymentRank;
    const paymentRanks = result.get(issuerId);
    if (paymentRanks == null) {
      result.set(issuerId, [paymentRank]);
      return result;
    }
    if (paymentRanks.includes(paymentRank)) {
      return result;
    }
    result.set(issuerId, paymentRanks.concat(paymentRank));
    return result;
  }, new Map());

  return pricedBonds
    .reduce<IssuerEntity[]>((result, pricedBond) => {
      const issuerId = pricedBond.issuerId;
      const paymentRanks = issuersWithPaymentRanks.get(issuerId) ?? [];
      // If issuer has only one paymentRank, add a single entry, otherwise ...
      if (paymentRanks.length < 2) {
        return result.concat({
          id: issuerIdWithPaymentRank(issuerId, paymentRanks[0]),
          ticker: pricedBond.issuerTicker,
          name: pricedBond.issuerName,
        });
      }
      // ... add one entry for every paymentRank in issuer.
      return [
        ...result,
        ...paymentRanks.map((paymentRank) => ({
          id: issuerIdWithPaymentRank(issuerId, paymentRank),
          paymentRank,
          ticker: pricedBond.issuerTicker,
          name: pricedBond.issuerName,
        })),
      ];
    }, [])
    .filter(
      // Deduplicate issuers by id.
      (issuer, index, array) => index === array.findIndex(({ id }) => issuer.id === id),
    )
    .sort(
      // Sort by name.
      ({ name: a }, { name: b }) => {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      },
    );
}
